import React, { useEffect, useState } from 'react';
import { supabase } from '../integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { useAuth } from '@/hooks/useAuth';

export default function WelcomeLNI() {
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { user } = useAuth();
  const hasSeenWelcome = user?.user_metadata?.has_seen_welcome === true;

  useEffect(() => {
    async function fetchVideoUrl() {
      try {
        const { data: { session } } = await supabase.auth.getSession();

        if (!session) {
          setTimeout(fetchVideoUrl, 1000);
          return;
        }

        const { data, error } = await supabase
          .storage
          .from('lni-media')
          .createSignedUrl('LNI-Formazione.mp4', 3600);

        if (error) throw error;
        setVideoUrl(data.signedUrl);
      } catch (error) {
        console.error('Errore nel caricamento del video:', error);
      } finally {
        setLoading(false);
      }
    }

    fetchVideoUrl();
  }, []);

  const handleComplete = async () => {
    try {
      // Registra nel profilo utente che la presentazione è stata vista
      const { error } = await supabase.auth.updateUser({
        data: { has_seen_welcome: true }
      });
      
      if (error) console.error("Impossibile aggiornare i metadati utente:", error);
    } finally {
      // Indirizza alla dashboard a prescindere dall'esito, per non bloccare l'utente
      navigate('/dashboard'); 
    }
  };

  return (
    <Layout>
    <div className="min-h-screen bg-background text-foreground p-6 flex flex-col items-center">
      <div className="max-w-4xl w-full bg-card text-card-foreground shadow-lg rounded-xl p-8 md:p-12 mt-10 border border-border">
        
        {/* Intestazione */}
        <header className="border-b border-border pb-6 mb-8 text-center md:text-left">
          <h1 className="text-3xl font-bold text-primary mb-2">Portale didattico della Lega Navale Italiana</h1>
          <h2 className="text-xl text-muted-foreground">a cura del Centro Culturale per la Formazione Nautica</h2>
        </header>

        {/* Corpo della presentazione con spaziatura automatica */}
        <div className="space-y-10 text-lg leading-relaxed">
          
          <section className="space-y-4">
            <p>
              <small><i>È possibile saltare questa presentazione premendo il pulsante in fondo alla pagina.<br />La si potrà ritrovare in seguito, selezionandola dal menu a sinistra.</i></small>
            </p>
            <h3 className="text-2xl font-semibold text-primary">Due mondi: sport e didattica</h3>
            <p>
              La LNI <strong>promuove lo sport</strong> secondo le direttive federali e <strong>divulga la cultura del mare</strong> in ossequio al proprio Statuto.
            </p>
            <p>
              Tuttavia, nella <strong>pratica agonistica</strong> vige l'esenzione dalle dotazioni di legge e sono richieste le dotazioni indicate dal Comitato Organizzatore, che redige il piano di sicurezza e se ne assume la responsabilità.
            </p>
            <p>
              Nella <strong>didattica e nella pratica marinaresca</strong>, invece, la sicurezza è l'aspetto primario e dirimente: gli allievi e le allieve imparano da subito a pianificare in autonomia la navigazione e a rendersene responsabili.
            </p>
          </section>

          <section className="space-y-4">
            <h3 className="text-2xl font-semibold text-primary">Il metodo: apprendimento esperienziale</h3>
            <p>
              La Lega Navale Italiana ha adottato l'architettura pedagogica pevista dal metodo del <i>Ciclo dell'apprendimento esperienziale</i> sviluppato dal pedagogista David Kolb, secondo cui l'apprendimento nasce dalla trasformazione dell'esperienza diretta in conoscenza, secondo quattro fasi:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4 text-muted-foreground marker:text-primary">
              <li>esperienza concreta</li>
              <li>osservazione riflessiva</li>
              <li>concettualizzazione astratta</li>
              <li>sperimentazione attiva</li>
            </ul>
            <p>
              e poi il ciclo si ripete, ripartendo, con ulteriori esperienze concrete, dal nuovo livello di apprendimento che si è raggiunto.
            </p>
            <p className="font-medium text-foreground">
              Dall'esperienza alla competenza: è il metodo su cui è impostata la didattica della LNI.
            </p>
          </section>

          <section className="space-y-4">
            <h3 className="text-2xl font-semibold text-primary">Tre strumenti</h3>
            <p>Per tradurre la missione in pratica, la LNI ha:</p>
            <ul className="list-disc list-inside space-y-2 ml-4 text-muted-foreground marker:text-primary">
              <li>messo a punto il <strong>Sistema unificato</strong> per la formazione di tutti i ruoli: dal Formatore ai vari livelli di Istruttore, secondo le discipline;</li>
              <li>elaborato il <strong>Manuale elettronico</strong> per Formatori e Istruttori;</li>
              <li>sviluppato questo <strong>Portale online</strong> per migliorare l'istruzione impartita, il quale può essere utilizzato sia in modalità di <i>didattica capovolta (flipped classroom)</i> sia in modalità tradizionale (lezioni frontali).</li>
            </ul>
          </section>

        </div>

        {/* Sezione Video */}
        <div className="mt-12 pt-8 border-t border-border">
          <h3 className="text-2xl font-semibold text-primary mb-6 text-center">Esplora le principali funzioni del Manuale elettronico online</h3>
          
          {loading ? (
            <div className="w-full h-64 flex items-center justify-center bg-muted rounded-lg">
              <span className="text-muted-foreground">Caricamento video in corso...</span>
            </div>
          ) : videoUrl ? (
            <div className="rounded-lg overflow-hidden shadow-md ring-1 ring-border">
              <video 
                key={videoUrl}
                controls 
                controlsList="nodownload" 
                className="w-full h-auto bg-black"
              >
                <source src={videoUrl} type="video/mp4" />
                Il tuo browser non supporta la riproduzione video.
              </video>
            </div>
          ) : (
            <div className="text-destructive text-center font-medium">Impossibile caricare il video protetto.</div>
          )}
        </div>

        {/* Azione Finale */}
        <div className="mt-12 text-center">
          <button 
            onClick={handleComplete}
            className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold py-3 px-8 rounded-lg shadow-sm transition-colors"
          >
            {hasSeenWelcome ? "Torna al pannello" : "Vai al pannello"}
          </button>
        </div>

      </div>
    </div>
    </Layout>
  );
}